import type { Change, HookCallback, TableKey } from '../../types/database'
import type { Content } from '../../types/content'
import { db } from '../services/db'
import type Dexie from 'dexie'
import { liveQuery } from 'dexie'

export function useDatabase() {
  const subscriptions: Array<() => void> = []

  async function get<T extends Content>(table: TableKey): Promise<T[]> {
    return db[table].toArray() as unknown as T[]
  }

  async function getItems<T extends Content>(key: TableKey): Promise<T[]> {
    return db[key]
      .filter((item: Content) => item.deletedAt === null)
      .toArray() as unknown as T[]
  }

  async function getItemsByContext<T extends Content>(
    key: TableKey,
    contextId: string
  ): Promise<T[]> {
    return db[key]
      .where('contextId')
      .equals(contextId)
      .and((item: Content) => !item.deletedAt)
      .toArray() as unknown as T[]
  }

  async function getItemById<T extends Content>(
    key: TableKey,
    id: string
  ): Promise<T | undefined> {
    return db[key].get(id) as Promise<T | undefined>
  }

  async function getContext<Context>(id: string): Promise<Context | null> {
    return db.contexts.where('id').equals(id).first() as Promise<Context | null>
  }

  /**
   * Adds multiple items to a table in the database in bulk.
   * This function is useful for seeding the database or for importing data.
   * @param key - The table to add items to
   * @param items - An array of items to add
   * @returns A promise that resolves when the items are added
   */
  async function addInBulk(key: TableKey, items: Content[]): Promise<void> {
    if (items.length === 0) return
    await (db[key] as Dexie.Table<Content>).bulkAdd(items)
  }

  async function addItem(
    key: TableKey,
    item: Omit<
      Content,
      'id' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'syncedAt'
    >
  ): Promise<Content> {
    const newItem = {
      ...item,
      id: crypto.randomUUID(),
      createdAt: Date.now(),
      updatedAt: Date.now(),
      deletedAt: null,
      syncedAt: null
    } as Content
    await (db[key] as Dexie.Table<Content>).add(newItem)
    return newItem
  }

  async function addItemWithId(key: TableKey, item: Content): Promise<Content> {
    await (db[key] as Dexie.Table<Content>).add(item)
    return item
  }

  async function updateInBulk(key: TableKey, items: Content[]): Promise<void> {
    if (items.length === 0) return
    // Transform to Dexie's bulkUpdate format
    const updates = items.map((item) => ({
      key: item.id,
      changes: {
        ...item,
        updatedAt: Date.now(),
        syncedAt: null
      }
    }))

    await (db[key] as Dexie.Table<Content>).bulkUpdate(updates)
  }

  async function updateItem(
    key: TableKey,
    item: Partial<Content & { id: string }>
  ): Promise<Content | undefined> {
    if (!item.id) return
    const id = item.id
    const isUpdated = await (db[key] as Dexie.Table<Content>).update(id, {
      ...item,
      updatedAt: Date.now()
    })
    if (!isUpdated) {
      console.warn(`updateItem: no record found for id ${id}`)
      return undefined
    }
    const updatedItem = await getItemById(key, id)
    return updatedItem
  }

  async function deleteItem(key: TableKey, id: string): Promise<void> {
    await db[key].delete(id)
  }

  async function deleteContext(id: string): Promise<void> {
    const context = await db.contexts.where('id').equals(id).first()

    if (!context) {
      console.warn(`Context with id ${id} not found`)
      return
    }

    await db.transaction(
      'rw',
      [db.contexts, db.notes, db.lists, db.tasks, db.entries],
      async () => {
        await Promise.all([
          db.notes.where('contextId').equals(context.id).delete(),
          db.lists.where('contextId').equals(context.id).delete(),
          db.tasks.where('contextId').equals(context.id).delete(),
          db.entries.where('contextId').equals(context.id).delete(),
          db.contexts.delete(context.id)
        ])
      }
    )
  }

  async function clearLocalDatabase(): Promise<void> {
    await Promise.all(db.tables.map((table: Dexie.Table) => table.clear()))
    window.location.reload()
  }

  async function getChanges() {
    return db.changes.toArray()
  }

  async function getChange(
    table: TableKey,
    itemId: string
  ): Promise<Change | null> {
    const result = (await db.changes.get({
      table: table,
      itemId: itemId
    })) as Change
    return result ?? null
  }

  async function clearChanges(): Promise<void> {
    await db.changes.clear()
  }

  async function addChange(change: Change): Promise<void> {
    await db.changes.add(change)
  }

  async function deleteChange(id: string): Promise<void> {
    await db.changes.where('id').equals(id).delete()
  }

  async function deleteChangesByIds(ids: string[]): Promise<void> {
    await Promise.all(ids.map((id) => deleteChange(id)))
  }

  async function syncUpdate(
    key: TableKey,
    item: Partial<Content & { id: string }>
  ): Promise<void> {
    if (!item.id) return
    await (db[key] as Dexie.Table<Content>).update(item.id, item)
  }

  async function markSynced(key: TableKey, id: string): Promise<void> {
    await (db[key] as Dexie.Table<Content>).update(id, {
      syncedAt: Date.now()
    })
  }

  async function updateChange(id: string): Promise<void> {
    await db.changes.update(id, { timestamp: Date.now() })
  }

  async function getPreference(key: string): Promise<string | null> {
    const preference = await db.preferences.get(key)
    return preference?.value ?? null
  }

  async function setPreference(key: string, value: string): Promise<void> {
    await db.preferences.put({ key, value }, key)
  }

  function subscribeTo<T extends Content>(
    table: TableKey,
    contextId: string | null = null
  ) {
    const observable = liveQuery(async () => {
      if (contextId !== null) {
        return db[table]
          .where('contextId')
          .equals(contextId)
          .filter((item: Content) => item.deletedAt === null)
          .toArray() as unknown as T[]
      }
      return db[table].toArray() as unknown as T[]
    })

    return observable
  }

  /**
   * Subscribes to Dexie CRUD hooks on all syncable tables.
   * The same callback is invoked for create, update and delete events.
   * Update events that only touch `syncedAt` are silently ignored to
   * prevent sync-loop feedback.
   * @param onChange - Called with the table name and primary key on every relevant change.
   */
  function subscribeToChanges(onChange: HookCallback) {
    const tables: TableKey[] = [
      'contexts',
      'notes',
      'tasks',
      'lists',
      'labels',
      'entries'
    ]

    for (const table of tables) {
      const createHandler = (primKey: string) => onChange(table, primKey)
      const updateHandler = (mods: object, primKey: string) => {
        // Skip sync-metadata-only updates (e.g. only syncedAt changed)
        const contentKeys = Object.keys(mods).filter((k) => k !== 'syncedAt')
        if (contentKeys.length === 0) return
        onChange(table, primKey)
      }
      const deleteHandler = (primKey: string) => onChange(table, primKey)

      db[table].hook('creating', createHandler)
      db[table].hook('updating', updateHandler)
      db[table].hook('deleting', deleteHandler)

      subscriptions.push(
        () => db[table].hook('creating').unsubscribe(createHandler),
        () => db[table].hook('updating').unsubscribe(updateHandler),
        () => db[table].hook('deleting').unsubscribe(deleteHandler)
      )
    }
  }

  function unsubscribeFromChanges() {
    subscriptions.forEach((unsub) => unsub())
    subscriptions.length = 0
  }

  return {
    db,
    get,
    getItemsByContext,
    getItems,
    subscribeTo,
    getItemById,
    getContext,
    addInBulk,
    addItem,
    addItemWithId,
    updateInBulk,
    updateItem,
    deleteItem,
    deleteContext,
    clearLocalDatabase,
    getChanges,
    getChange,
    clearChanges,
    addChange,
    deleteChange,
    deleteChangesByIds,
    syncUpdate,
    markSynced,
    updateChange,
    getPreference,
    setPreference,
    subscribeToChanges,
    unsubscribeFromChanges
  }
}
